import React, { useEffect, useMemo, useRef, useState } from 'react';
import styles from '../FolderItem/FoldersItem.module.scss';
import Col from '../GridSystem/Col';
import { Card } from '../Card';
import CardHeader from '../Card/CardHeader';
import Avatar from '../Avatar/Avatar';
import CardTitle from '../Card/CardTitle';
import { useClipsContext } from '../../context/ClipsContext';

import CardFooter from '../Card/CardFooter';
import Highlighter from 'react-highlight-words';
import NumberCheckbox from '../NumberCheckbox/NumberCheckbox';
import cx from 'classnames';
import Tooltip from '../Tooltip/Tooltip';
import { FOLDER_USER_ROLE } from '../../constants/UserRoles';
import { useTranslation } from 'react-i18next';
import UsersListPreview from '../UsersListPreview/UsersListPreview';
import { useUserContext } from '../../context/UserContext';

const FoldersItemClips = ({ folder, folderIndex, handleCardClick, searchInput, containerRef }) => {
  const [titleHover, setTitleHover] = useState({});
  const [folderClipsNumber, setFolderClipsNumber] = useState(0);

  const { foldersWithClips } = useClipsContext();
  const { user } = useUserContext();

  const parent = useRef();

  const { t } = useTranslation();

  const isFolderOwner = user?.username === folder?.owner?.username;
  const isFolderEditable = !!folder?.editingEnabled;

  const isViewer = folder?.users?.find(
    ({ username, folderRole }) => username === user?.username && folderRole === FOLDER_USER_ROLE.VIEWER.TYPE
  );

  const isEditor = folder?.users?.find(
    ({ username, folderRole }) => username === user?.username && folderRole === FOLDER_USER_ROLE.EDITOR.TYPE
  );

  const isFolderAdmin = isEditor && isFolderEditable;

  const ownershipLabel = useMemo(() => {
    let role = '';
    if (isViewer) {
      role = 'viewer';
    }
    if (isEditor && !isFolderAdmin) {
      role = 'editor';
    }
    if (isFolderAdmin) {
      role = 'admin';
    }
    if (!!folder?.users?.length && isFolderOwner) {
      role = 'owner';
    }
    return role ? <div className={cx(styles[role])}>{t(`folder.role.${role}`)}</div> : null;
  }, [isViewer, isEditor, isFolderAdmin, isFolderOwner, folder, t]);

  useEffect(() => {
    setFolderClipsNumber(foldersWithClips[folder.id]);
  }, [foldersWithClips]);

  const toggleMouseEnterTitle = videoIndex => {
    setTitleHover({ index: videoIndex, isHover: true });
  };

  const toggleMouseLeaveTitle = videoIndex => {
    setTitleHover({ index: videoIndex, isHover: false });
  };

  const onCardClick = () => {
    handleCardClick(folder.id);
  };

  return !isViewer ? (
    <Col xs={12} sm={3} md={3} lg={3} xl={3} className={styles.cards}>
      <Card
        data-cy="video"
        onClick={onCardClick}
        className={cx(styles.folderCard, { [styles.activeCardBgcOnly]: Boolean(foldersWithClips[folder.id]) })}
      >
        <CardHeader>
          <CardTitle innerRef={parent}>
            {Boolean(foldersWithClips[folder.id]) ? (
              <NumberCheckbox number={folderClipsNumber} />
            ) : (
              <div className={styles.usersDetails}>
                <div className={styles.avatars}>
                  <UsersListPreview owner={folder?.owner} user={user} users={folder?.users ?? []} parentRef={parent} />
                </div>
                {ownershipLabel}
              </div>
            )}
          </CardTitle>
        </CardHeader>
        <CardFooter
          className={cx(styles.footerCard)}
          onMouseEnter={() => toggleMouseEnterTitle(folderIndex)}
          onMouseLeave={() => toggleMouseLeaveTitle(folderIndex)}
        >
          <>
            <div className={styles.cardTitleContainer}>
              <CardTitle className={cx(styles.cardTitle)}>
                <Highlighter
                  highlightClassName="wordHighlighter"
                  searchWords={searchInput?.trim().length > 0 ? [searchInput] : ['']}
                  autoEscape
                  textToHighlight={folder.name}
                  className={styles.highlighterCard}
                />
              </CardTitle>
              <Tooltip isHover={titleHover.isHover} fullwidth={true} moveable={false}>
                <Highlighter
                  highlightClassName="wordHighlighter"
                  searchWords={searchInput?.trim().length > 0 ? [searchInput] : ['']}
                  autoEscape
                  textToHighlight={folder.name}
                  className={styles.hoverText}
                />
              </Tooltip>
            </div>
          </>
          <div className={styles.footerExtra}>
            <span className={styles.createdAtField}>{folder.createdAt}</span>
            {folder.storyCount > 0 && <span> {`| ${folder.storyCount} video${folder.storyCount === 1 ? '' : 's'}`}</span>}
          </div>
        </CardFooter>
      </Card>
    </Col>
  ) : null;
};

export default FoldersItemClips;
